import React, { ComponentProps, Dispatch, SetStateAction } from 'react';
import { EcSalesListingLine as entity } from '../../graphql/entities/vat/EcSalesListingLine';
import Workspace from '../../components/Workspace';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { FilterOperation, IFilterInput, IEcSalesListingLine, IFinancialPeriod } from '../../graphql/schema/p4types';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import useEcSalesWorkspaceActions from '../../actions/customer/EcSalesWorkspaceAction';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import getDocumentModalProps from '../../utils/getDocumentModalProps';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { IWorkspaceProps } from '../../@types/workspaceTypes';
import { Maybe } from 'graphql/jsutils/Maybe';

const LazyFinancialTransactionModal = React.lazy(() => import('../../components/modals/FinancialTransactionModal'));

const EC_SALES_LISTING_LINE_FIELD_NAMES = [
  'isoCountryCode',
  'customer.name',
  'customer.reference',
  'vatRegistrationNumber',
  'amount',
  'indicator',
  'documentNumber',
];

const availableFiltersFilter = (column: IEditableDataGridColumnDef) => {
  return column?.fieldName !== 'ecSalesListing';
};

const columnOverrides = {
  documentNumber: {
    name: 'Source No',
    formatter: ModalFormatter,
    width: 125,
    options: {
      modalContent: LazyFinancialTransactionModal,
      entityId: 'sourceId',
      modalProps: getDocumentModalProps,
    },
  },
};

const EcSalesListingLineWorkspace = (
  props: IWorkspaceProps<IEcSalesListingLine> & {
    jobId: string;
    setJobId: Dispatch<SetStateAction<string>>;
    fromFinancialPeriod: Maybe<IFinancialPeriod>;
    toFinancialPeriod: Maybe<IFinancialPeriod>;
  }
) => {
  const { jobId, setJobId, fromFinancialPeriod, toFinancialPeriod } = props;
  const filters: Array<IFilterInput> = [
    {
      filterParameters: {
        field: 'ecSalesListing.jobId',
        value: jobId ?? 0,
      },
      filterOperation: FilterOperation.Equals,
    },
  ];

  const workspaceProps = useWorkspace(props, entity, {
    columnOverrides: columnOverrides,
    columns: EC_SALES_LISTING_LINE_FIELD_NAMES,
    filters: filters,
  });

  const EuSalesWorkspaceActions = (props: Omit<ComponentProps<typeof WorkspaceActionButtons>, 'reportButtons'>) => {
    const [generateAction, reportActions, modals] = useEcSalesWorkspaceActions(
      fromFinancialPeriod,
      toFinancialPeriod,
      setJobId
    );
    return (
      <>
        {modals}
        <WorkspaceActionButtons {...props} actionButtons={[generateAction]} reportButtons={reportActions} />
      </>
    );
  };

  return (
    <Workspace
      availableFiltersFilter={availableFiltersFilter}
      {...workspaceProps}
      {...props}
      entity={entity}
      contentHeight={window.innerHeight - 400}
      workspaceActionButtonsHOC={EuSalesWorkspaceActions}
    />
  );
};

export default EcSalesListingLineWorkspace;
